import React from 'react';
import type {
  BreakdownCell,
  BreakdownCellContext,
} from '@/components/ui/breakdown-tables/types';
import { formatCurrency } from '@erp/shared-utils';
import { cn } from '@/lib/utils';
import { FormattedInput } from '@/components/ui/formatted-input';

/**
 * Tolerance for floating-point comparisons in allocation amounts (1 cent)
 * Used to determine if amounts are effectively zero or equal within rounding error
 */
const ALLOCATION_AMOUNT_TOLERANCE = 0.01;

/**
 * Common aggregation function for cell values (sums numeric values)
 * Used by both EditableAllocationBreakdownTable and ReconciliationBreakdownTable
 */
export function aggregateCellValues(
  values: BreakdownCell['value'][],
): BreakdownCell['value'] {
  return values
    .filter((v) => v !== null && v !== undefined && typeof v === 'number')
    .reduce((sum, val) => sum + (val as number), 0);
}

/**
 * Filter cells that match a given aggregation scope (row/column)
 * Used by both EditableAllocationBreakdownTable and ReconciliationBreakdownTable
 */
export function filterCellsForAggregation(
  data: BreakdownCell[],
  row: string | undefined,
  column: string | undefined,
  isGroupLevel: boolean,
): BreakdownCell[] {
  return data.filter((cell) => {
    // Match row: if __TOTAL__, match all rows; if group level, match specific row; otherwise match exact row
    const matchesRow =
      row === '__TOTAL__'
        ? true // Grand total: include all rows
        : isGroupLevel && row
          ? cell.row === row // Group-level aggregation: match specific row
          : !row || cell.row === row; // Exact match or no row filter

    // Match column: if provided, match exact column; otherwise include all columns
    const matchesColumn = !column || cell.column === column;

    return matchesRow && matchesColumn;
  });
}

/**
 * Determine allocation status for color coding
 */
export function getAllocationStatus(
  expectedRebate: number,
  totalAllocation: number,
): 'matched' | 'partial' | 'unallocated' | 'zero-needed' {
  // Gray: No rebate needed ($0 expected)
  if (Math.abs(expectedRebate) < ALLOCATION_AMOUNT_TOLERANCE) {
    return 'zero-needed';
  }
  // Green: Fully allocated (within tolerance)
  if (totalAllocation >= expectedRebate - ALLOCATION_AMOUNT_TOLERANCE) {
    return 'matched';
  }
  // Yellow: Partially allocated
  if (totalAllocation > ALLOCATION_AMOUNT_TOLERANCE) {
    return 'partial';
  }
  // Red: Unallocated (expected > 0 but allocation = 0)
  return 'unallocated';
}

export interface AllocationCellData {
  expectedRebate: number;
  allocated: number; // For read-only: total allocated. For editable: currentAllocation
  globalAllocation?: number; // Only used in editable mode
  delta: number;
}

export interface RenderAllocationCellOptions {
  /** Whether the cell is editable */
  editable: boolean;
  /** Callback when allocation changes (required if editable) */
  onAllocationChange?: (
    ruleId: string,
    divisionId: string,
    date: string,
    amount: number,
  ) => void;
  /** Metadata from cell (required if editable) */
  metadata?: {
    ruleId?: string;
    divisionId?: string;
    date?: string;
  };
}

/**
 * Shared cell renderer for allocation breakdown tables
 * Supports both editable and read-only modes
 */
export function renderAllocationCell(
  context: BreakdownCellContext,
  data: AllocationCellData,
  options: RenderAllocationCellOptions,
): React.ReactNode {
  const { cell, row, isGroupLevel } = context;
  const { editable, onAllocationChange, metadata } = options;
  const { expectedRebate, allocated, globalAllocation, delta } = data;

  const isAggregated = isGroupLevel || row === '__TOTAL__';
  const totalAllocation =
    editable && globalAllocation !== undefined
      ? allocated + globalAllocation
      : allocated;

  const status = getAllocationStatus(expectedRebate, totalAllocation);

  // Color coding based on allocation status
  const statusColors = {
    matched: 'bg-green-50 border-green-200',
    partial: 'bg-yellow-50 border-yellow-200',
    unallocated: 'bg-red-50 border-red-200',
    'zero-needed': 'bg-gray-50 border-gray-200',
  };

  return (
    <div
      className={cn(
        'flex flex-col gap-1 py-1 min-w-[120px] px-2 rounded border',
        statusColors[status],
      )}
    >
      {isAggregated ? (
        <>
          {/* Aggregated: Show totals */}
          <div className="text-xs font-medium">
            Total: {formatCurrency(allocated)}
          </div>
          {/* Expected rebate (middle) */}
          <div className="text-xs text-muted-foreground">
            Exp: {formatCurrency(expectedRebate)}
            {editable &&
              globalAllocation !== undefined &&
              globalAllocation > 0 && (
                <span className="ml-2">
                  Alloc: {formatCurrency(globalAllocation)}
                </span>
              )}
          </div>
          {/* Delta (bottom) */}
          <div
            className={cn(
              'text-xs font-medium',
              Math.abs(delta) < ALLOCATION_AMOUNT_TOLERANCE
                ? 'text-muted-foreground'
                : delta > 0
                  ? 'text-green-600'
                  : 'text-red-600',
            )}
          >
            {delta >= 0 ? '+' : ''}
            {formatCurrency(delta)}
          </div>
        </>
      ) : (
        <>
          {/* Allocation (top) - editable or read-only */}
          {editable &&
          onAllocationChange &&
          metadata?.ruleId &&
          metadata?.divisionId &&
          metadata?.date ? (
            <FormattedInput
              formatType="currency"
              value={allocated.toString()}
              placeholder="0.00"
              onChange={(value) => {
                const newValue = parseFloat(value) || 0;
                onAllocationChange(
                  metadata.ruleId!,
                  metadata.divisionId!,
                  metadata.date!,
                  newValue,
                );
              }}
              className="h-7 text-xs bg-background"
            />
          ) : (
            <div className="text-xs font-medium">
              Alloc: {formatCurrency(allocated)}
            </div>
          )}
          {/* Expected rebate (middle) */}
          <div className="text-xs text-muted-foreground">
            Exp: {formatCurrency(expectedRebate)}
            {editable &&
              globalAllocation !== undefined &&
              globalAllocation > 0 && (
                <span className="ml-2">
                  Alloc: {formatCurrency(globalAllocation)}
                </span>
              )}
          </div>
          {/* Delta (bottom) */}
          <div
            className={cn(
              'text-xs font-medium',
              Math.abs(delta) < ALLOCATION_AMOUNT_TOLERANCE
                ? 'text-muted-foreground'
                : delta > 0
                  ? 'text-green-600'
                  : 'text-red-600',
            )}
          >
            {delta >= 0 ? '+' : ''}
            {formatCurrency(delta)}
          </div>
        </>
      )}
    </div>
  );
}
